import { supabase } from "@/integrations/supabase/client";
import { 
  startOfMonth, 
  endOfMonth, 
  eachDayOfInterval, 
  format, 
  addMonths,
  isSameDay,
  getDay 
} from "date-fns";
import { toZonedTime } from "date-fns-tz";

// 🎯 CACHE SYSTEM - Cache simples para sessões agendadas
interface CacheEntry {
  data: ScheduledSession[];
  timestamp: number;
  mentorId: string;
}

const CACHE_DURATION = 2 * 60 * 1000; // 2 minutos
const sessionCache = new Map<string, CacheEntry>();

// Fuso horário do Brasil (São Paulo)
const BRAZIL_TIMEZONE = 'America/Sao_Paulo';

export interface MentorAvailabilitySlot {
  id: string;
  mentor_id: string;
  day_of_week: number;
  start_time: string;
  end_time: string;
  interval_minutes: number;
}

export interface ScheduledSession {
  id: string;
  scheduled_date: string;
  scheduled_start: string;
  scheduled_end: string;
  status: string;
}

export const fetchMentorAvailability = async (mentorId: string): Promise<MentorAvailabilitySlot[]> => {
  const { data, error } = await supabase
    .from("mentor_availability")
    .select("*")
    .eq("mentor_id", mentorId);

  if (error) {
    console.error("Error fetching mentor availability:", error);
    throw error;
  }

  return data || [];
};

// OPTIMIZED: Fetch scheduled sessions for a mentor with cache
export const fetchMentorScheduledSessions = async (mentorId: string): Promise<ScheduledSession[]> => {
  // 🎯 Verificar cache primeiro
  const cacheKey = `sessions_${mentorId}`;
  const cached = sessionCache.get(cacheKey);
  
  if (cached && (Date.now() - cached.timestamp) < CACHE_DURATION) {
    console.log(`🚀 Cache hit for mentor ${mentorId} sessions`);
    return cached.data;
  }

  // Cache miss ou expirado - buscar do banco
  console.log(`🔄 Fetching fresh sessions for mentor ${mentorId}`);
  const { data, error } = await supabase
    .from("sessions")
    .select("id, scheduled_date, scheduled_start, scheduled_end, status")
    .eq("mentor_id", mentorId)
    .in("status", ["confirmado", "pending"]) // ✅ CORRIGIDO: usar status em português
    .gte("scheduled_date", format(new Date(), 'yyyy-MM-dd')); // Only future sessions

  if (error) {
    console.error("Error fetching scheduled sessions:", error);
    throw error;
  }

  const sessions = data || [];
  
  // 🎯 Armazenar no cache
  sessionCache.set(cacheKey, {
    data: sessions,
    timestamp: Date.now(),
    mentorId
  });

  return sessions;
};

// 🎯 CACHE MANAGEMENT - Função para limpar cache quando necessário
export const clearMentorSessionsCache = (mentorId?: string) => {
  if (mentorId) {
    const cacheKey = `sessions_${mentorId}`;
    sessionCache.delete(cacheKey);
    console.log(`🗑️ Cache cleared for mentor ${mentorId}`);
  } else {
    sessionCache.clear();
    console.log(`🗑️ All sessions cache cleared`);
  }
};

// 🎯 NOVA FUNÇÃO: Forçar refresh do cache após correções
export const forceRefreshMentorSessions = async (mentorId: string): Promise<ScheduledSession[]> => {
  // Limpar cache primeiro
  clearMentorSessionsCache(mentorId);
  
  // Buscar dados frescos
  console.log(`🔄 Force refresh sessions for mentor ${mentorId}`);
  const { data, error } = await supabase
    .from("sessions")
    .select("id, scheduled_date, scheduled_start, scheduled_end, status")
    .eq("mentor_id", mentorId)
    .in("status", ["confirmado", "pending"]) // ✅ Status corretos
    .gte("scheduled_date", format(new Date(), 'yyyy-MM-dd'));

  if (error) {
    console.error("Error in force refresh:", error);
    throw error;
  }

  const sessions = data || [];
  
  // Atualizar cache com dados frescos
  const cacheKey = `sessions_${mentorId}`;
  sessionCache.set(cacheKey, {
    data: sessions,
    timestamp: Date.now(),
    mentorId
  });

  console.log(`✅ Cache refreshed with ${sessions.length} sessions for mentor ${mentorId}`);
  return sessions;
};

// Check if a specific time slot conflicts with existing sessions
export const isTimeSlotBooked = (
  date: Date,
  startTime: string,
  endTime: string,
  scheduledSessions: ScheduledSession[]
): boolean => {
  const dateString = format(date, 'yyyy-MM-dd');
  
  return scheduledSessions.some(session => {
    if (session.scheduled_date !== dateString) return false;
    
    // Check for time overlap
    const sessionStart = session.scheduled_start;
    const sessionEnd = session.scheduled_end;
    
    // Two time slots overlap if:
    // startTime < sessionEnd && endTime > sessionStart
    return startTime < sessionEnd && endTime > sessionStart;
  });
};

/**
 * Verifica se um horário já passou considerando o fuso horário de São Paulo
 * @param date Data selecionada
 * @param timeSlot Objeto com startTime e endTime (formato "HH:mm")
 * @returns true se o horário já passou, false caso contrário
 */
export const isTimeSlotInPast = (
  date: Date,
  timeSlot: { startTime: string; endTime: string }
): boolean => {
  try {
    // Obter data/hora atual em SP
    const nowInSP = toZonedTime(new Date(), BRAZIL_TIMEZONE);
    const selectedDateInSP = toZonedTime(date, BRAZIL_TIMEZONE);
    
    // Se não for o mesmo dia, não é passado (já foi filtrado por data)
    if (!isSameDay(nowInSP, selectedDateInSP)) {
      return false;
    }
    
    // Se for o mesmo dia, verificar se o horário já passou
    const [hours, minutes] = timeSlot.startTime.split(':').map(Number);
    
    // Criar data/hora do slot no fuso de SP
    const slotDateTime = new Date(selectedDateInSP);
    slotDateTime.setHours(hours, minutes, 0, 0);
    
    // Comparar com horário atual em SP
    return slotDateTime < nowInSP;
  } catch (error) {
    console.error('Erro ao verificar se horário passou:', error);
    // Em caso de erro, retornar false para não bloquear horários válidos
    return false;
  }
};

// Utility function to get occurrences of weekdays in a specific date range
export const getOccurrencesOfDayInRange = (dayOfWeek: number, startDate: Date, endDate: Date): Date[] => {
  const dates: Date[] = [];
  const allDays = eachDayOfInterval({ start: startDate, end: endDate });
  
  allDays.forEach(date => {
    if (getDay(date) === dayOfWeek) {
      dates.push(date);
    }
  });
  
  return dates;
};

// Get available dates for a specific month
export const getAvailableDatesForMonth = (
  availabilitySlots: MentorAvailabilitySlot[], 
  targetDate: Date
): Date[] => {
  const monthStart = startOfMonth(targetDate);
  const monthEnd = endOfMonth(targetDate);
  const availableDates: Date[] = [];
  
  // For each availability slot, get occurrences in the target month
  availabilitySlots.forEach(slot => {
    const dates = getOccurrencesOfDayInRange(slot.day_of_week, monthStart, monthEnd);
    availableDates.push(...dates);
  });
  
  // Sort dates and remove duplicates
  return availableDates
    .sort((a, b) => a.getTime() - b.getTime())
    .filter((date, index, self) => 
      index === self.findIndex((d) => isSameDay(d, date))
    );
};

// Check if a specific date is available
export const isDateAvailable = (
  date: Date, 
  availabilitySlots: MentorAvailabilitySlot[]
): boolean => {
  const dayOfWeek = getDay(date);
  return availabilitySlots.some(slot => slot.day_of_week === dayOfWeek);
};

// Get available dates based on mentor availability (for multiple months)
export const getAvailableDatesForMentor = (
  availabilitySlots: MentorAvailabilitySlot[], 
  currentViewDate: Date = new Date()
): Date[] => {
  const availableDates: Date[] = [];
  
  // Generate dates for current month and next 5 months (6 months total)
  for (let i = 0; i < 6; i++) {
    const targetMonth = addMonths(currentViewDate, i);
    const monthDates = getAvailableDatesForMonth(availabilitySlots, targetMonth);
    availableDates.push(...monthDates);
  }
  
  // Sort dates and remove duplicates
  return availableDates
    .sort((a, b) => a.getTime() - b.getTime())
    .filter((date, index, self) => 
      index === self.findIndex((d) => isSameDay(d, date))
    );
};

// Get time slots for a specific date based on mentor availability - WITHOUT checking for booked sessions
export const getTimeSlotsForDate = (
  date: Date, 
  availabilitySlots: MentorAvailabilitySlot[]
): { startTime: string; endTime: string }[] => {
  const dayOfWeek = getDay(date);
  
  // Filter slots for this day of week
  const slotsForDay = availabilitySlots.filter(slot => slot.day_of_week === dayOfWeek);
  
  if (slotsForDay.length === 0) return [];
  
  const timeSlots: { startTime: string; endTime: string }[] = [];
  
  // For each availability slot, generate time slots based on interval
  slotsForDay.forEach(slot => {
    const [startHours, startMinutes] = slot.start_time.split(':').map(Number);
    const [endHours, endMinutes] = slot.end_time.split(':').map(Number);
    
    const startDate = new Date();
    startDate.setHours(startHours, startMinutes, 0, 0);
    
    const endDate = new Date();
    endDate.setHours(endHours, endMinutes, 0, 0);
    
    const intervalMinutes = slot.interval_minutes || 60;
    
    let currentTime = new Date(startDate);
    
    while (currentTime.getTime() + intervalMinutes * 60000 <= endDate.getTime()) {
      const endTimeSlot = new Date(currentTime.getTime() + intervalMinutes * 60000);
      
      timeSlots.push({
        startTime: `${currentTime.getHours().toString().padStart(2, '0')}:${currentTime.getMinutes().toString().padStart(2, '0')}`,
        endTime: `${endTimeSlot.getHours().toString().padStart(2, '0')}:${endTimeSlot.getMinutes().toString().padStart(2, '0')}`
      });
      
      // Move to next slot
      currentTime.setTime(currentTime.getTime() + intervalMinutes * 60000);
    }
  });
  
  return timeSlots;
};

// OPTIMIZED FUNCTION: Get available time slots for a specific date - CONSIDERING booked sessions AND past times
export const getAvailableTimeSlotsForDate = async (
  date: Date, 
  mentorId: string,
  availabilitySlots: MentorAvailabilitySlot[],
  cachedScheduledSessions?: ScheduledSession[] // 🎯 Parâmetro opcional para cache
): Promise<{ startTime: string; endTime: string }[]> => {
  // Get all possible time slots for this date
  const allTimeSlots = getTimeSlotsForDate(date, availabilitySlots);
  
  // Use cached sessions if provided, otherwise fetch from database
  const scheduledSessions = cachedScheduledSessions || await fetchMentorScheduledSessions(mentorId);
  
  // Filter out booked time slots AND past time slots
  const availableTimeSlots = allTimeSlots.filter(slot => {
    // Verificar se já foi agendado
    const isBooked = isTimeSlotBooked(date, slot.startTime, slot.endTime, scheduledSessions);
    
    // Verificar se já passou (mesmo dia)
    const isPast = isTimeSlotInPast(date, slot);
    
    return !isBooked && !isPast;
  });
  
  return availableTimeSlots;
};

// NEW FUNCTION: Check if a date has any available time slots (considering booked sessions AND past times) - LOCAL VERSION
const hasAvailableTimeSlotsForDateLocal = (
  date: Date,
  availabilitySlots: MentorAvailabilitySlot[],
  scheduledSessions: ScheduledSession[]
): boolean => {
  const allTimeSlots = getTimeSlotsForDate(date, availabilitySlots);
  
  const availableTimeSlots = allTimeSlots.filter(slot => {
    // Verificar se já foi agendado
    const isBooked = isTimeSlotBooked(date, slot.startTime, slot.endTime, scheduledSessions);
    
    // Verificar se já passou (mesmo dia)
    const isPast = isTimeSlotInPast(date, slot);
    
    return !isBooked && !isPast;
  });
  
  return availableTimeSlots.length > 0;
};

// NEW FUNCTION: Check if a date has any available time slots (considering booked sessions)
export const hasAvailableTimeSlotsForDate = async (
  date: Date,
  mentorId: string,
  availabilitySlots: MentorAvailabilitySlot[]
): Promise<boolean> => {
  const availableTimeSlots = await getAvailableTimeSlotsForDate(date, mentorId, availabilitySlots);
  return availableTimeSlots.length > 0;
};

// OPTIMIZED FUNCTION: Get available dates with time slot availability check - SINGLE DB QUERY
export const getAvailableDatesForMentorWithSessions = async (
  mentorId: string,
  availabilitySlots: MentorAvailabilitySlot[], 
  currentViewDate: Date = new Date()
): Promise<Date[]> => {
  const potentialDates = getAvailableDatesForMentor(availabilitySlots, currentViewDate);
  
  // 🎯 OTIMIZAÇÃO: Buscar todas as sessões de uma vez só
  const scheduledSessions = await fetchMentorScheduledSessions(mentorId);
  const availableDates: Date[] = [];
  
  // Verificar cada data usando os dados já carregados (sem novas consultas ao banco)
  for (const date of potentialDates) {
    const hasSlots = hasAvailableTimeSlotsForDateLocal(date, availabilitySlots, scheduledSessions);
    if (hasSlots) {
      availableDates.push(date);
    }
  }
  
  return availableDates;
};

// Legacy function for backward compatibility
export const getNextOccurrencesOfDay = (dayOfWeek: number, occurrences = 4): Date[] => {
  const today = new Date();
  const nextMonth = addMonths(today, 1);
  return getOccurrencesOfDayInRange(dayOfWeek, today, nextMonth).slice(0, occurrences);
};
